import { ANNOTATION_COLORS } from "../tokens";
// Drawing geometry and export match the mobile PhotoAnnotationOverlay.
import type { PhotoAnnotationBatch, PhotoAnnotationFocus } from "../../../../backend/src/shared/types";

export type AnnotationTool = "draw" | "arrow" | "rect" | "text";
export type AnnotationColor = typeof ANNOTATION_COLORS[keyof typeof ANNOTATION_COLORS];

interface AnnotationPoint {
  x: number;
  y: number;
}

interface PathAnnotation {
  id: string;
  type: "path";
  color: AnnotationColor;
  width: number;
  points: AnnotationPoint[];
}

interface ArrowAnnotation {
  id: string;
  type: "arrow";
  color: AnnotationColor;
  width: number;
  from: AnnotationPoint;
  to: AnnotationPoint;
}

interface RectAnnotation {
  id: string;
  type: "rect";
  color: AnnotationColor;
  width: number;
  from: AnnotationPoint;
  to: AnnotationPoint;
}

interface TextAnnotation {
  id: string;
  type: "text";
  color: AnnotationColor;
  size: number;
  point: AnnotationPoint;
  text: string;
}

export type Annotation =
  | PathAnnotation
  | ArrowAnnotation
  | RectAnnotation
  | TextAnnotation;

export type DraftAnnotation =
  | Omit<PathAnnotation, "id">
  | Omit<ArrowAnnotation, "id">
  | Omit<RectAnnotation, "id">
  | null;

export const COLOR_OPTIONS: AnnotationColor[] = Object.values(ANNOTATION_COLORS);

function clamp01(value: number): number {
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

function createAnnotationId(): string {
  return `annotation_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function pointToPixels(
  point: AnnotationPoint,
  width: number,
  height: number,
): { x: number; y: number } {
  return {
    x: point.x * width,
    y: point.y * height,
  };
}

function buildPathData(points: AnnotationPoint[], width: number, height: number): string {
  if (points.length === 0) {
    return "";
  }

  return points
    .map((point, index) => {
      const pixelPoint = pointToPixels(point, width, height);
      return `${index === 0 ? "M" : "L"} ${pixelPoint.x} ${pixelPoint.y}`;
    })
    .join(" ");
}

function getRectBounds(
  from: AnnotationPoint,
  to: AnnotationPoint,
): { x: number; y: number; width: number; height: number } {
  return {
    x: Math.min(from.x, to.x),
    y: Math.min(from.y, to.y),
    width: Math.abs(to.x - from.x),
    height: Math.abs(to.y - from.y),
  };
}

function getPointBounds(points: AnnotationPoint[]) {
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return {
    x,
    y,
    width: Math.max(0.000001, Math.max(...xs) - x),
    height: Math.max(0.000001, Math.max(...ys) - y),
  };
}

export function buildPhotoAnnotationBatch(
  annotations: Annotation[],
): PhotoAnnotationBatch {
  const focuses: PhotoAnnotationFocus[] = [];
  let textCharacters = 0;

  for (const annotation of annotations.slice(0, 50)) {
    if (annotation.type === "path") {
      if (annotation.points.length >= 2) {
        focuses.push({ type: "path", bounds: getPointBounds(annotation.points) });
      }
      continue;
    }
    if (annotation.type === "rect") {
      focuses.push({
        type: "rect",
        bounds: getRectBounds(annotation.from, annotation.to),
      });
      continue;
    }
    if (annotation.type === "arrow") {
      focuses.push({
        type: "arrow",
        target: { ...annotation.to },
        bounds: getPointBounds([annotation.from, annotation.to]),
      });
      continue;
    }

    const remaining = 1_000 - textCharacters;
    const text = annotation.text.trim().slice(0, Math.min(200, remaining));
    if (text) {
      focuses.push({ type: "text", anchor: { ...annotation.point }, text });
      textCharacters += text.length;
    }
  }

  return {
    id:
      globalThis.crypto?.randomUUID?.() ??
      `annotation_batch_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    focuses,
  };
}

function drawArrowHead(
  ctx: CanvasRenderingContext2D,
  from: { x: number; y: number },
  to: { x: number; y: number },
  size: number,
) {
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const headAngle = Math.PI / 7;

  ctx.beginPath();
  ctx.moveTo(to.x, to.y);
  ctx.lineTo(
    to.x - size * Math.cos(angle - headAngle),
    to.y - size * Math.sin(angle - headAngle),
  );
  ctx.moveTo(to.x, to.y);
  ctx.lineTo(
    to.x - size * Math.cos(angle + headAngle),
    to.y - size * Math.sin(angle + headAngle),
  );
  ctx.stroke();
}

export function renderAnnotationToCanvas(
  ctx: CanvasRenderingContext2D,
  annotation: Annotation,
  width: number,
  height: number,
) {
  if (annotation.type === "text") {
    const point = pointToPixels(annotation.point, width, height);
    const fontSize = Math.max(22, width * annotation.size);
    ctx.font = `700 ${fontSize}px Inter, sans-serif`;
    ctx.textBaseline = "top";
    ctx.lineJoin = "round";
    ctx.lineWidth = Math.max(3, fontSize * 0.16);
    ctx.strokeStyle = annotation.color === ANNOTATION_COLORS.white ? "rgba(16,16,16,0.45)" : "rgba(255,255,255,0.78)";
    ctx.strokeText(annotation.text, point.x, point.y);
    ctx.fillStyle = annotation.color;
    ctx.fillText(annotation.text, point.x, point.y);
    return;
  }

  const strokeWidth = Math.max(3, width * annotation.width);
  ctx.strokeStyle = annotation.color;
  ctx.lineWidth = strokeWidth;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  if (annotation.type === "path") {
    if (annotation.points.length < 2) {
      return;
    }
    ctx.beginPath();
    annotation.points.forEach((point, index) => {
      const pixelPoint = pointToPixels(point, width, height);
      if (index === 0) {
        ctx.moveTo(pixelPoint.x, pixelPoint.y);
      } else {
        ctx.lineTo(pixelPoint.x, pixelPoint.y);
      }
    });
    ctx.stroke();
    return;
  }

  const from = pointToPixels(annotation.from, width, height);
  const to = pointToPixels(annotation.to, width, height);

  if (annotation.type === "rect") {
    ctx.strokeRect(
      Math.min(from.x, to.x),
      Math.min(from.y, to.y),
      Math.abs(to.x - from.x),
      Math.abs(to.y - from.y),
    );
    return;
  }

  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(to.x, to.y);
  ctx.stroke();
  drawArrowHead(ctx, from, to, Math.max(12, strokeWidth * 3.2));
}

function drawDraftToCanvas(
  ctx: CanvasRenderingContext2D,
  draft: DraftAnnotation,
  width: number,
  height: number,
) {
  if (!draft) {
    return;
  }

  renderAnnotationToCanvas(ctx, { ...draft, id: "draft" } as Annotation, width, height);
}
